// Service Stripe complet avec intégration backend

// Fonction pour valider le numéro de carte (algorithme de Luhn)
export const validateCardNumber = (cardNumber) => {
  if (!cardNumber) return false;
  const cleaned = cardNumber.replace(/\s/g, '');
  if (cleaned.length < 13 || cleaned.length > 19) {
    return false;
  }
  let sum = 0;
  let isEven = false;
  for (let i = cleaned.length - 1; i >= 0; i--) {
    let digit = parseInt(cleaned.charAt(i));
    if (isNaN(digit)) return false;
    if (isEven) {
      digit *= 2;
      if (digit > 9) {
        digit -= 9;
      }
    }
    sum += digit;
    isEven = !isEven;
  }
  return sum % 10 === 0;
};

// Fonction pour détecter le type de carte
export const detectCardType = (cardNumber) => {
  if (!cardNumber) return 'unknown';
  const cleaned = cardNumber.replace(/\s/g, '');
  if (/^4/.test(cleaned)) { return 'visa'; }
  if (/^5[1-5]/.test(cleaned)) { return 'mastercard'; }
  if (/^3[47]/.test(cleaned)) { return 'amex'; }
  if (/^6(?:011|5)/.test(cleaned)) { return 'discover'; }
  return 'unknown';
};

// Fonction pour initialiser Stripe de manière sécurisée
export const getStripe = async () => {
  try {
    const { loadStripe } = await import('@stripe/stripe-js');
    
    // Importer la configuration
    const { getStripePublishableKey } = await import('../config/stripe');
    const STRIPE_PUBLISHABLE_KEY = getStripePublishableKey();
    
    if (!STRIPE_PUBLISHABLE_KEY) {
      console.warn('⚠️ Clé Stripe non configurée');
      return null;
    }
    
    console.log('🔑 Clé Stripe trouvée:', STRIPE_PUBLISHABLE_KEY.substring(0, 20) + '...');
    return await loadStripe(STRIPE_PUBLISHABLE_KEY);
  } catch (error) {
    console.error('Erreur lors du chargement de Stripe:', error);
    return null;
  }
};

// Fonction pour créer un PaymentIntent via l'API backend
export const createPaymentIntent = async (amount, currency = 'eur', description = null, metadata = {}) => {
  try {
    // Vérifier si l'utilisateur est connecté
    const token = localStorage.getItem('access_token');
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    
    // Préparer les données de paiement selon le schéma backend
    const paymentData = {
      amount: Math.round(amount * 100),
      currency,
      description,
      items: metadata.items || metadata.order_items || [],
      delivery_address: metadata.delivery_address || '',
      delivery_instructions: metadata.delivery_instructions || '',
      order_type: metadata.order_type || 'delivery',
      delivery_fee: metadata.delivery_fee != null ? Number(metadata.delivery_fee) : null,
    };
    
    console.log('📤 Données envoyées à l\'API:', paymentData);
    
    // Importer la configuration pour l'URL de l'API
    const { getApiUrl } = await import('../config/stripe');
    const apiUrl = getApiUrl();
    
    const response = await fetch(`${apiUrl}/api/stripe/create-payment-intent`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        // Le backend rattache la commande au client via le token (jamais via un user_id envoyé)
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(paymentData)
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error('❌ Erreur API:', JSON.stringify(errorData, null, 2));
      
      // Construire un message d'erreur détaillé
      let errorMessage = `Erreur ${response.status}: ${response.statusText}`;
      
      if (errorData.detail) {
        if (Array.isArray(errorData.detail)) {
          // Erreurs de validation Pydantic
          errorMessage = errorData.detail.map(err => 
            `${err.loc?.join('.')}: ${err.msg}`
          ).join(', ');
        } else {
          errorMessage = errorData.detail;
        }
      }
      
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error('Erreur lors de la création du PaymentIntent:', error);
    throw error;
  }
};

// Fonction pour confirmer un paiement via l'API backend
export const confirmPayment = async (paymentIntentId, paymentMethodId) => {
  try {
    // Importer la configuration pour l'URL de l'API
    const { getApiUrl } = await import('../config/stripe');
    const apiUrl = getApiUrl();
    
    const response = await fetch(`${apiUrl}/api/stripe/confirm-payment`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        payment_intent_id: paymentIntentId,
        payment_method_id: paymentMethodId
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Erreur lors de la confirmation du paiement');
    }

    return await response.json();
  } catch (error) {
    console.error('Erreur lors de la confirmation du paiement:', error);
    throw error;
  }
};

// Fonction pour récupérer le statut d'un paiement
export const getPaymentStatus = async (paymentIntentId) => {
  try {
    // Importer la configuration pour l'URL de l'API
    const { getApiUrl } = await import('../config/stripe');
    const apiUrl = getApiUrl();
    
    const response = await fetch(`${apiUrl}/api/stripe/payment-status/${paymentIntentId}`);
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Erreur lors de la récupération du statut');
    }

    return await response.json();
  } catch (error) {
    console.error('Erreur lors de la récupération du statut:', error);
    throw error;
  }
};

// Fonction pour tester la configuration Stripe
export const testStripeConfig = async () => {
  try {
    // Importer la configuration pour l'URL de l'API
    const { getApiUrl } = await import('../config/stripe');
    const apiUrl = getApiUrl();
    
    const response = await fetch(`${apiUrl}/api/stripe/test`);
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Erreur lors du test de configuration');
    }

    return await response.json();
  } catch (error) {
    console.error('Erreur lors du test de configuration Stripe:', error);
    throw error;
  }
};

// Fonction pour traiter un paiement complet
export const processPayment = async (amount, currency = 'eur', description = null, metadata = {}) => {
  try {
    // 1. Créer le PaymentIntent
    const paymentIntent = await createPaymentIntent(amount, currency, description, metadata);
    
    // 2. Initialiser Stripe
    const stripe = await getStripe();
    if (!stripe) {
      throw new Error('Stripe non configuré');
    }

    // 3. Confirmer le paiement avec Stripe
    const { error } = await stripe.confirmCardPayment(paymentIntent.client_secret, {
      payment_method: {
        card: {
          // Les détails de la carte seront fournis par le composant Stripe Elements
        },
      }
    });

    if (error) {
      throw new Error(error.message);
    }

    return {
      success: true,
      paymentIntentId: paymentIntent.payment_intent_id,
      status: 'succeeded'
    };

  } catch (error) {
    console.error('Erreur lors du traitement du paiement:', error);
    throw error;
  }
};
